import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import ReactDOM from "react-dom";
import { dataService } from "../../../../../Services/data/data.service";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip } from "@mui/material";
// import admin from "firebase-admin";
import { Factory, Repeat, X, Plus, Trash2, Search, CircleOff, Check, ChevronDown, Percent, Hash, Clock } from "lucide-react";
import { crudTypes, eventTypes, currencySymbols } from "../../../../../Config/constants";
// components
import CustomCheckbox from "../../../../Shared/Checkbox";
import Button from "../../../../Shared/Buttons/Button";
import Textarea from "../../../../Shared/Textarea";
import Chip from "../../../../Shared/ui/Chip";
import Input from "../../../../Shared/Input";
import Label from "../../../../Shared/Label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectGroup, SelectLabel } from "../../../../Shared/Select";
import Divider from "../../../../Shared/ui/Divider";

export default function PriceBundleDialog({ dialogOpen, closeDialog, priceBundle, crudType, ...props }) {
  const { t } = useTranslation();
  const classes = useStyles();

  const [generalError, setGeneralError] = useState(null);
  const [disclaimer, setDisclaimer] = useState(null);

  const [schedulingPriceChange, setSchedulingPriceChange] = useState(false);

  // Data
  const [allCompanies, setAllCompanies] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [allCostBundleTypes, setAllCostBundleTypes] = useState([]);
  const [allPriceDefinitions, setAllPriceDefinitions] = useState([]);

  // Cost Bundle Type Selection
  const [selectedCostBundleType, setSelectedCostBundleType] = useState(null);
  const [selectedCostBundleTypeError, setSelectedCostBundleTypeError] = useState(null);

  const [isRecurring, setIsRecurring] = useState(false);

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedCompanyError, setSelectedCompanyError] = useState(null);

  const [reference, setReference] = useState(null);
  const [referenceError, setReferenceError] = useState(null);

  const [name, setName] = useState(null);
  const [nameError, setNameError] = useState(null);

  const [description, setDescription] = useState(null);
  const [descriptionError, setDescriptionError] = useState(null);

  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedAccountError, setSelectedAccountError] = useState(null);

  const [price, setPrice] = useState(null);
  const [priceError, setPriceError] = useState(null);

  const [uom, setUom] = useState(null);
  const [uomError, setUomError] = useState(null);

  const [recurrencePeriod, setRecurrencePeriod] = useState(null);
  const [recurrencePeriodError, setRecurrencePeriodError] = useState(null);

  const [createAnotherBundle, setCreateAnotherBundle] = useState(false);

  // Confirmation state for deleting price entries
  const [confirmDelete, setConfirmDelete] = useState(null); // stores the priceChangeId to delete

  // Confirmation state for deleting the entire price bundle
  const [confirmDeleteBundle, setConfirmDeleteBundle] = useState(false);

  // Price Definitions multiselect (similar to ProjectionsDialog)
  const [priceDefinitionSearchValue, setPriceDefinitionSearchValue] = useState("");
  const [priceDefinitionDropdownOpen, setPriceDefinitionDropdownOpen] = useState(false);
  const [selectedPriceDefinitions, setSelectedPriceDefinitions] = useState([]);
  const [selectedPriceDefinitionsError, setSelectedPriceDefinitionsError] = useState(null);

  // Store input display values separately for better UX
  const [multiplierInputValues, setMultiplierInputValues] = useState({});

  // Fixed costs multiselect (TIME bundles only): applied once per measurement, not scaled by duration
  const [fixedPriceDefinitionSearchValue, setFixedPriceDefinitionSearchValue] = useState("");
  const [fixedPriceDefinitionDropdownOpen, setFixedPriceDefinitionDropdownOpen] = useState(false);
  const [selectedFixedPriceDefinitions, setSelectedFixedPriceDefinitions] = useState([]);
  const [fixedMultiplierInputValues, setFixedMultiplierInputValues] = useState({});

  // Price definition dropdown logic
  const [priceDefDropdownPosition, setPriceDefDropdownPosition] = useState({ top: 0, left: 0 });
  const [priceDefDropdownWidth, setPriceDefDropdownWidth] = useState(0);
  const priceDefDropdownRef = useRef(null);
  const priceDefOpenDropdownButtonRef = useRef(null);
  const dialogContentRef = useRef(null);

  // Fixed costs dropdown logic
  const [fixedPriceDefDropdownPosition, setFixedPriceDefDropdownPosition] = useState({ top: 0, left: 0 });
  const [fixedPriceDefDropdownWidth, setFixedPriceDefDropdownWidth] = useState(0);
  const fixedPriceDefDropdownRef = useRef(null);
  const fixedPriceDefOpenDropdownButtonRef = useRef(null);

  // Get account from allAccounts
  const getAccountById = (accountId) => {
    if (!allAccounts || allAccounts.length === 0) return null;
    for (const company of allAccounts) {
      const account = company.accountCategories.flatMap((category) => category.accounts).find((acc) => acc.id === accountId);
      if (account) {
        return account;
      }
    }
    return null;
  };

  // Partitions the bundle's items into the hourly and fixed selections and seeds both multiplier inputs
  const loadPriceDefinitionSelections = (priceBundleItems) => {
    const mapItems = (items) =>
      items
        .map((pbi) => {
          const priceDefinition = allPriceDefinitions.find((pd) => pd.id === pbi.priceDefinitionId);
          if (!priceDefinition) {
            console.warn(`Price definition with id ${pbi.priceDefinitionId} not found`);
            return null;
          }
          return {
            ...priceDefinition,
            multiplier: pbi.multiplier || 1,
          };
        })
        .filter(Boolean); // Remove any null/undefined entries

    const hourlyDefinitions = mapItems(priceBundleItems.filter((pbi) => !pbi.isFixed));
    const fixedDefinitions = mapItems(priceBundleItems.filter((pbi) => pbi.isFixed));

    setSelectedPriceDefinitions(hourlyDefinitions);
    setSelectedFixedPriceDefinitions(fixedDefinitions);

    const inputValues = {};
    hourlyDefinitions.forEach((pd) => {
      inputValues[pd.id] = (pd.multiplier || 1).toString();
    });
    setMultiplierInputValues(inputValues);

    const fixedInputValues = {};
    fixedDefinitions.forEach((pd) => {
      fixedInputValues[pd.id] = (pd.multiplier || 1).toString();
    });
    setFixedMultiplierInputValues(fixedInputValues);
  };

  const fetchCostBundleTypes = () => {
    dataService
      .getAllCostBundleTypes()
      .then((response) => {
        if (response) {
          setAllCostBundleTypes(response);
        } else {
          console.error("Error fetching cost bundle types:", response);
          setGeneralError(t('errors.generic'));
        }
      })
      .catch((error) => {
        console.error("Error fetching cost bundle styles:", error);
        setGeneralError(t('errors.generic'));
      });
  };

  const fetchPriceDefinitions = (userId) => {
    dataService
      .getAllPriceDefinitionsWithCurrentPrice(userId)
      .then((response) => {
        if (response) {
          setAllPriceDefinitions(response);
        } else {
          console.error("Error fetching price definitions:", response);
          setGeneralError(t('errors.generic'));
        }
      })
      .catch((error) => {
        console.error("Error fetching price definitions:", error);
        setGeneralError(t('errors.generic'));
      });
  };

  useEffect(() => {
    if (props.Login && props.Login.userid && dialogOpen) {
      // Fetch cost bundle types
      fetchCostBundleTypes();

      // Fetch price definitions only when dialog is open
      fetchPriceDefinitions(props.Login.userid);

      // Get all companies
      dataService
        .getAllCompaniesByUserId(props.Login.userid)
        .then((response) => {
          setAllCompanies(response);
          if (response.length === 1) {
            setSelectedCompany(response[0]);
          }
        })
        .catch((error) => {
          console.error("Error fetching companies:", error);
          setGeneralError(t('errors.generic'));
        });

      // Get all accountCategories & accounts
      dataService
        .getAllUserCompanyAccounts(props.Login.userid, 0, 0)
        .then((response) => {
          setAllAccounts(response);
        })
        .catch((error) => {
          console.error("Error fetching account categories:", error);
          setGeneralError(t('errors.generic'));
        });
    }
  }, [props.Login.userid, dialogOpen]);

  // Price Definition Dropdown Logic
  const updateDropdownPosition = () => {
    if (priceDefOpenDropdownButtonRef.current) {
      const rect = priceDefOpenDropdownButtonRef.current.getBoundingClientRect();

      setPriceDefDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });

      setPriceDefDropdownWidth(rect.width);
    }
  };

  useEffect(() => {
    if (priceDefinitionDropdownOpen) {
      updateDropdownPosition();

      // Update position on scroll and resize
      const handleScroll = () => updateDropdownPosition();
      const handleResize = () => updateDropdownPosition();

      // Add event listeners
      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleResize);

      // ResizeObserver to detect dialog content changes
      let resizeObserver;
      let dialogResizeObserver;

      if (priceDefOpenDropdownButtonRef.current) {
        resizeObserver = new ResizeObserver(() => {
          updateDropdownPosition();
        });
        resizeObserver.observe(priceDefOpenDropdownButtonRef.current);
      }

      // Also observe the dialog content container for resize changes
      if (dialogContentRef.current) {
        dialogResizeObserver = new ResizeObserver(() => {
          updateDropdownPosition();
        });
        dialogResizeObserver.observe(dialogContentRef.current);
      }

      // Cleanup function
      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleResize);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
        if (dialogResizeObserver) {
          dialogResizeObserver.disconnect();
        }
      };
    }
  }, [priceDefinitionDropdownOpen]);

  useEffect(() => {
    const handleClickOutsidePriceDefDropdown = (event) => {
      if (priceDefDropdownRef.current && !priceDefDropdownRef.current.contains(event.target) && priceDefinitionDropdownOpen) {
        if (!priceDefOpenDropdownButtonRef.current.contains(event.target)) {
          setPriceDefinitionDropdownOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutsidePriceDefDropdown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsidePriceDefDropdown);
    };
  }, [priceDefinitionDropdownOpen]);

  // Fixed costs dropdown position + click outside (mirrors the main price definitions dropdown)
  const updateFixedDropdownPosition = () => {
    if (fixedPriceDefOpenDropdownButtonRef.current) {
      const rect = fixedPriceDefOpenDropdownButtonRef.current.getBoundingClientRect();

      setFixedPriceDefDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });

      setFixedPriceDefDropdownWidth(rect.width);
    }
  };

  useEffect(() => {
    if (fixedPriceDefinitionDropdownOpen) {
      updateFixedDropdownPosition();

      const handleScroll = () => updateFixedDropdownPosition();
      const handleResize = () => updateFixedDropdownPosition();

      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleResize);

      let resizeObserver;
      let dialogResizeObserver;

      if (fixedPriceDefOpenDropdownButtonRef.current) {
        resizeObserver = new ResizeObserver(() => {
          updateFixedDropdownPosition();
        });
        resizeObserver.observe(fixedPriceDefOpenDropdownButtonRef.current);
      }

      if (dialogContentRef.current) {
        dialogResizeObserver = new ResizeObserver(() => {
          updateFixedDropdownPosition();
        });
        dialogResizeObserver.observe(dialogContentRef.current);
      }

      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleResize);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
        if (dialogResizeObserver) {
          dialogResizeObserver.disconnect();
        }
      };
    }
  }, [fixedPriceDefinitionDropdownOpen]);

  useEffect(() => {
    const handleClickOutsideFixedPriceDefDropdown = (event) => {
      if (fixedPriceDefDropdownRef.current && !fixedPriceDefDropdownRef.current.contains(event.target) && fixedPriceDefinitionDropdownOpen) {
        if (!fixedPriceDefOpenDropdownButtonRef.current.contains(event.target)) {
          setFixedPriceDefinitionDropdownOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutsideFixedPriceDefDropdown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideFixedPriceDefDropdown);
    };
  }, [fixedPriceDefinitionDropdownOpen]);

  useEffect(() => {
    resetErrors();
    resetFields();
    if (crudType === crudTypes.CREATE) {
      console.log("Creating new price bundle");
    } else if (crudType === crudTypes.UPDATE) {
      if (priceBundle && allPriceDefinitions.length > 0 && allCompanies.length > 0 && allAccounts.length > 0 && allCostBundleTypes.length > 0) {
        console.log("PriceBundle: ", priceBundle);
        setSelectedCompany(allCompanies.find((company) => company.id === priceBundle.companyId));
        setSelectedAccount(getAccountById(priceBundle.account.id));
        setSelectedCostBundleType(allCostBundleTypes.find((type) => type.id === priceBundle.costBundleTypeId));
        setReference(priceBundle.reference);
        setName(priceBundle.name);
        setDescription(priceBundle.description);

        // Load selected price definitions if they exist
        if (priceBundle.priceBundleItems && priceBundle.priceBundleItems.length > 0) {
          loadPriceDefinitionSelections(priceBundle.priceBundleItems);
        }
      }
    } else if (crudType === crudTypes.READ) {
      if (priceBundle && allPriceDefinitions.length > 0 && allCompanies.length > 0 && allAccounts.length > 0 && allCostBundleTypes.length > 0) {
        // Add check for allPriceDefinitions
        setSelectedCompany(allCompanies.find((company) => company.id === priceBundle.companyId));
        setSelectedAccount(getAccountById(priceBundle.account.id));
        setSelectedCostBundleType(allCostBundleTypes.find((type) => type.id === priceBundle.costBundleTypeId));
        setReference(priceBundle.reference);
        setName(priceBundle.name);
        setDescription(priceBundle.description);
        setPrice(priceBundle.currentPrice?.price || null);
        setUom(priceBundle.currentPrice?.unitOfMeasurement || null);
        setIsRecurring(priceBundle.isRecurring);

        // Load selected price definitions if they exist
        if (priceBundle.priceBundleItems && priceBundle.priceBundleItems.length > 0) {
          loadPriceDefinitionSelections(priceBundle.priceBundleItems);
        }
      }
    } else {
      setDisclaimer(t('errors.generic'));
    }
  }, [crudType, priceBundle, allCompanies, allAccounts, allCostBundleTypes, allPriceDefinitions]);

  const getDialogTitle = () => {
    switch (crudType) {
      case crudTypes.CREATE:
      case crudTypes.UPDATE:
        return t('common.edit') + (priceBundle ? ` ${priceBundle.name}` : "");
      case crudTypes.READ:
        return priceBundle ? `${priceBundle.reference} - ${priceBundle.name}` : t('priceBundle.priceBundle');
      default:
        return "Price Bundle";
    }
  };

  const getSubmitLabel = () => {
    switch (crudType) {
      case crudTypes.CREATE:
      case crudTypes.UPDATE:
        return t('priceBundle.saveChanges');
      default:
        return t('common.submit');
    }
  };

  const changeSelectedCompany = (company) => {
    setSelectedCompany(company);
    setSelectedAccount(null);
  };

  const changeReference = (e) => {
    setReference(e.target.value);
    if (e.target.value.length < 1) {
      setReference(null);
    }
  };

  const changeName = (e) => {
    setName(e.target.value);
    if (e.target.value.length < 1) {
      setName(null);
    }
  };

  const changeDescription = (e) => {
    setDescription(e.target.value);
    if (e.target.value.length < 1) {
      setDescription(null);
    }
  };

  const changePrice = (e) => {
    setPrice(e.target.value);
    if (e.target.value.length < 1) {
      setPrice(null);
    }
  };

  const changeUom = (e) => {
    setUom(e.target.value);
    if (e.target.value.length < 1) {
      setUom(null);
    }
  };

  const resetErrors = () => {
    setNameError(null);
    setReferenceError(null);
    setDescriptionError(null);
    setSelectedAccountError(null);
    setPriceError(null);
    setUomError(null);
    setRecurrencePeriodError(null);
    setSelectedCompanyError(null);
    setSelectedCostBundleTypeError(null);
    setSelectedPriceDefinitionsError(null);
    setGeneralError(null);
  };

  const resetFields = () => {
    setName("");
    setIsRecurring(false);
    setSelectedCompany(null);
    setReference("");
    setDescription("");
    setSelectedAccount(null);
    setPrice(null);
    setUom("");
    setRecurrencePeriod(null);
    setCreateAnotherBundle(false);
    setDisclaimer(null);
    setSelectedCostBundleType(null);
    setSelectedPriceDefinitions([]);
    setPriceDefinitionSearchValue("");
    setMultiplierInputValues({});
    setSelectedFixedPriceDefinitions([]);
    setFixedPriceDefinitionSearchValue("");
    setFixedMultiplierInputValues({});
  };

  const resetFieldsKeepCompany = () => {
    setName("");
    setIsRecurring(false);
    setReference("");
    setDescription("");
    setSelectedAccount(null);
    setPrice(null);
    setUom("");
    setRecurrencePeriod(null);
    setDisclaimer(null);
    setSelectedPriceDefinitions([]);
    setSelectedCostBundleType(null);
    setPriceDefinitionSearchValue("");
    setMultiplierInputValues({});
    setSelectedFixedPriceDefinitions([]);
    setFixedPriceDefinitionSearchValue("");
    setFixedMultiplierInputValues({});
    // Keep selectedCompany and createAnotherBundle unchanged
  };

  // Price Definition Selection Logic
  const handlePriceDefinitionSelection = (priceDefinition) => {
    let tempPriceDefinition = {
      multiplier: 1,
      ...priceDefinition,
    };

    if (selectedPriceDefinitions.some((pd) => pd.id === tempPriceDefinition.id)) {
      // if price definition is already selected, remove it
      setSelectedPriceDefinitions((prev) => prev.filter((pd) => pd.id !== tempPriceDefinition.id));
      // Also remove from input values
      setMultiplierInputValues((prev) => {
        const newValues = { ...prev };
        delete newValues[tempPriceDefinition.id];
        return newValues;
      });
    } else {
      // if price definition is not selected, add it
      setSelectedPriceDefinitions((prev) => [...prev, tempPriceDefinition]);
      // Initialize the input value
      setMultiplierInputValues((prev) => ({
        ...prev,
        [tempPriceDefinition.id]: "1",
      }));
    }
  };

  const setPriceDefinitionMultiplier = (priceDefinitionId, value) => {
    // Update the display value immediately
    setMultiplierInputValues((prev) => ({
      ...prev,
      [priceDefinitionId]: value,
    }));

    // Only update the actual multiplier if it's a valid number
    if (value !== "" && !isNaN(parseFloat(value))) {
      const numericValue = Math.max(0.0001, Math.min(9999999999.9999, parseFloat(value)));
      setSelectedPriceDefinitions((prev) => prev.map((pd) => (pd.id === priceDefinitionId ? { ...pd, multiplier: numericValue } : pd)));
    }
  };

  // Fixed costs selection logic (mirrors the hourly selection above)
  const handleFixedPriceDefinitionSelection = (priceDefinition) => {
    let tempPriceDefinition = {
      multiplier: 1,
      ...priceDefinition,
    };

    if (selectedFixedPriceDefinitions.some((pd) => pd.id === tempPriceDefinition.id)) {
      setSelectedFixedPriceDefinitions((prev) => prev.filter((pd) => pd.id !== tempPriceDefinition.id));
      setFixedMultiplierInputValues((prev) => {
        const newValues = { ...prev };
        delete newValues[tempPriceDefinition.id];
        return newValues;
      });
    } else {
      setSelectedFixedPriceDefinitions((prev) => [...prev, tempPriceDefinition]);
      setFixedMultiplierInputValues((prev) => ({
        ...prev,
        [tempPriceDefinition.id]: "1",
      }));
    }
  };

  const setFixedPriceDefinitionMultiplier = (priceDefinitionId, value) => {
    setFixedMultiplierInputValues((prev) => ({
      ...prev,
      [priceDefinitionId]: value,
    }));

    if (value !== "" && !isNaN(parseFloat(value))) {
      const numericValue = Math.max(0.0001, Math.min(9999999999.9999, parseFloat(value)));
      setSelectedFixedPriceDefinitions((prev) => prev.map((pd) => (pd.id === priceDefinitionId ? { ...pd, multiplier: numericValue } : pd)));
    }
  };

  // Selected indicator component
  const SelectedIndicator = ({ isSelected }) => {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        {isSelected ? (
          <div style={{ height: "20px", width: "20px", borderRadius: "50%", backgroundColor: "#5CC958", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Check size={16} color={"#FFFFFF"} />
          </div>
        ) : (
          <div style={{ height: "20px", width: "20px", borderRadius: "50%", backgroundColor: "#EDEDED", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Plus size={16} color={"#0C0E29"} />
          </div>
        )}
      </div>
    );
  };

  const handleCloseDialog = () => {
    setSchedulingPriceChange(false);
    setConfirmDeleteBundle(false);
    resetErrors();
    resetFields();
    closeDialog();
  };

  const handleDeletePriceBundle = () => {
    dataService
      .deletePriceBundle(priceBundle.id)
      .then((response) => {
        if (response.success) {
          toast.success(t('priceBundle.deletedSuccess'));
          if (props.onDelete) {
            props.onDelete(priceBundle.uniqueId);
          }
          setConfirmDeleteBundle(false);
          handleCloseDialog();
        } else {
          setGeneralError(response.message || t('errors.generic'));
          setConfirmDeleteBundle(false);
        }
      })
      .catch((error) => {
        setGeneralError(t('errors.generic'));
        setConfirmDeleteBundle(false);
      });
  };

  const handleSubmit = async () => {
    switch (crudType) {
      case crudTypes.CREATE:
        handleCreatePriceBundle();
        break;
      case crudTypes.UPDATE:
        handleUpdatePriceBundle();
        break;
      default:
        setGeneralError(t('errors.generic'));
        return;
    }
  };

  const handleCreatePriceBundle = async () => {
    const isValid = validateCreateFields();

    if (!isValid) {
      return;
    }
    // dataservice
    const newPriceBundle = {
      reference: reference,
      name: name,
      description: description,
      companyId: selectedCompany.id,
      accountId: selectedAccount.id,
      costBundleTypeId: selectedCostBundleType?.id,
      priceBundleItems: [
        ...selectedPriceDefinitions.map((pd) => ({
          PriceDefinitionId: pd.id,
          multiplier: pd.multiplier,
          isFixed: false,
        })),
        ...(selectedCostBundleType?.id === eventTypes.TIME_BASED
          ? selectedFixedPriceDefinitions.map((pd) => ({
              PriceDefinitionId: pd.id,
              multiplier: pd.multiplier,
              isFixed: true,
            }))
          : []),
      ],
      insertedByUserId: props.Login.userid,
    };

    dataService
      .createPriceBundle(newPriceBundle)
      .then((response) => {
        if (response.success) {
          toast.success(t('priceBundle.createdSuccess'));
          if (props.onAdd) {
            props.onAdd(response.priceBundle);
          }
          if (createAnotherBundle) {
            resetErrors();
            resetFieldsKeepCompany();
          } else {
            handleCloseDialog();
          }
        } else {
          setGeneralError(response.message || t('errors.generic'));
          return;
        }
      })
      .catch((error) => {
        setGeneralError(t('errors.generic'));
      });
  };

  const handleUpdatePriceBundle = async () => {
    const isValid = validateUpdateFields();
    if (!isValid) {
      return;
    }
    // dataservice
    const updatedPriceBundle = {
      uniqueId: priceBundle.uniqueId,
      reference: reference,
      name: name,
      description: description,
      accountId: selectedAccount.id,
      priceBundleItems: [
        ...selectedPriceDefinitions.map((pd) => ({
          PriceDefinitionId: pd.id,
          multiplier: pd.multiplier,
          isFixed: false,
        })),
        ...(selectedCostBundleType?.id === eventTypes.TIME_BASED
          ? selectedFixedPriceDefinitions.map((pd) => ({
              PriceDefinitionId: pd.id,
              multiplier: pd.multiplier,
              isFixed: true,
            }))
          : []),
      ],
      updatedByUserId: props.Login.userid,
    };
    dataService
      .updatePriceBundle(updatedPriceBundle)
      .then((response) => {
        if (response.success) {
          toast.success(t('priceBundle.updatedSuccess'));
          if (props.onUpdate) {
            props.onUpdate(response.priceBundle);
          }
          handleCloseDialog();
        } else {
          setGeneralError(response.message || t('errors.generic'));
          return;
        }
      })
      .catch((error) => {
        setGeneralError(t('errors.generic'));
      });
  };

  const validateCreateFields = () => {
    let isValid = true;
    resetErrors();
    if (!selectedCompany) {
      setSelectedCompanyError(t('common.required'));
      isValid = false;
    }
    if (!isRecurring && !selectedCostBundleType) {
      setSelectedCostBundleTypeError(t('common.required'));
      isValid = false;
    }
    if (!reference || reference.length < 1) {
      setReferenceError(t('common.required'));
      isValid = false;
    }
    if (!name || name.length < 1) {
      setNameError(t('common.required'));
      isValid = false;
    }
    if (!selectedAccount) {
      setSelectedAccountError(t('common.required'));
      isValid = false;
    }
    const hasFixedDefinitions = selectedCostBundleType?.id === eventTypes.TIME_BASED && selectedFixedPriceDefinitions.length > 0;
    if ((!selectedPriceDefinitions || selectedPriceDefinitions.length === 0) && !hasFixedDefinitions) {
      setSelectedPriceDefinitionsError(t('priceBundle.atLeastOnePriceDef'));
      isValid = false;
    }
    return isValid;
  };

  const validateUpdateFields = () => {
    let isValid = true;
    resetErrors();
    if (!reference || reference.length < 1) {
      setReferenceError(t('common.required'));
      isValid = false;
    }
    if (!name || name.length < 1) {
      setNameError(t('common.required'));
      isValid = false;
    }
    if (!selectedAccount) {
      setSelectedAccountError(t('common.required'));
      isValid = false;
    }
    const hasFixedDefinitions = selectedCostBundleType?.id === eventTypes.TIME_BASED && selectedFixedPriceDefinitions.length > 0;
    if ((!selectedPriceDefinitions || selectedPriceDefinitions.length === 0) && !hasFixedDefinitions) {
      setSelectedPriceDefinitionsError(t('priceBundle.atLeastOnePriceDef'));
      isValid = false;
    }
    return isValid;
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined || price === "") {
      return "-";
    } else {
      const handlePriceFormat = (price) => {
        return parseFloat(price)
          .toFixed(3) // Ensure three decimal places
          .replace(".", ",") // Replace decimal point with comma
          .replace(/\B(?=(\d{3})+(?!\d))/g, "."); // Add dot as thousands separator
      };

      return handlePriceFormat(price);
    }
  };

  // PriceDefinitionsDisplay component
  const PriceDefinitionsDisplay = ({ definitions }) => {
    if (!definitions || definitions.length === 0) return null;

    return (
      <div className={classes.priceDefinitionsContainer}>
        {definitions
          .sort((a, b) => a.code.localeCompare(b.code))
          .map((priceDefinition, index) => {
            return (
              <div className={classes.priceDefinitionItem} key={priceDefinition.id}>
                <p className={classes.priceDefinitionName}>
                  {priceDefinition.code} ({priceDefinition.name})
                </p>
                <div className={classes.priceDefinitionDetails}>
                  <div className={classes.multiplierContainer}>
                    <p className={classes.multiplierText}>{priceDefinition.multiplier}</p>
                    <X className={classes.multiplierIcon} />
                  </div>
                  <Chip>
                    {priceDefinition.currentPrice.currencySymbol}
                    {formatPrice(priceDefinition.currentPrice.price)}
                  </Chip>
                </div>
              </div>
            );
          })}
      </div>
    );
  };

  // totalCard
  const TotalCard = () => {
    const sumDefinitions = (definitions) => {
      return definitions.reduce((total, pd) => {
        const currentPrice = pd.currentPrice;
        return total + (currentPrice ? currentPrice.price * pd.multiplier : 0);
      }, 0);
    };

    const getTotalPrice = () => sumDefinitions(selectedPriceDefinitions);
    const getFixedTotalPrice = () => (selectedCostBundleType?.id === eventTypes.TIME_BASED ? sumDefinitions(selectedFixedPriceDefinitions) : 0);

    const getTotalIcon = () => {
      switch (selectedCostBundleType?.id) {
        case eventTypes.TIME_BASED:
          return <Clock strokeWidth={1.4} style={{ color: "inherit" }} size={24} />;
        case eventTypes.EVENT_BASED:
          return <Hash strokeWidth={1.4} style={{ color: "inherit" }} size={24} />;
        case eventTypes.RECURRING:
          return <Repeat strokeWidth={1.4} style={{ color: "inherit" }} size={24} />;
        default:
          return <CircleOff strokeWidth={1.4} style={{ color: "inherit" }} size={24} />;
      }
    };

    const getUnitLabel = () => {
      switch (selectedCostBundleType?.id) {
        case eventTypes.TIME_BASED:
          return t('priceBundle.unitHour');
        case eventTypes.EVENT_BASED:
          return t('priceBundle.unitEvent');
        case eventTypes.RECURRING:
          return t('priceBundle.unitMonth');
        default:
          return t('priceBundle.unitUnit');
      }
    };

    const getAccountStyles = () => {
      if (!selectedAccount) return {};

      switch (selectedAccount.isRevenue) {
        case true:
          return {
            backgroundColor: "#F3FBF2",
            borderColor: "#E2F5E0",
            color: "#257223",
          };
        case false:
          return {
            backgroundColor: "#FBEDED",
            borderColor: "#F8D3D3",
            color: "#993434",
          };
        default:
          return {};
      }
    };

    const fixedTotal = getFixedTotalPrice();

    return (
      <div className={classes.totalContainer} style={getAccountStyles()}>
        {getTotalIcon()}
        <p className={classes.totalText}>
          {selectedCompany?.currencySymbol || ""}
          {formatPrice(getTotalPrice())}/{getUnitLabel()}
          {fixedTotal > 0 && (
            <>
              {" + "}
              {selectedCompany?.currencySymbol || ""}
              {formatPrice(fixedTotal)} {t('priceBundle.fixedSuffix')}
            </>
          )}
        </p>
      </div>
    );
  };

  return (
    <>
    <Dialog className={classes.dialog} open={dialogOpen} aria-labelledby="draggable-dialog-title" classes={{ paper: classes.dialogPaper }}>
      <div className={classes.dialog__head}>
        <DialogTitle className={classes.dialog__title} id="draggable-dialog-title">
          {getDialogTitle()}
        </DialogTitle>
        <button onClick={() => handleCloseDialog()} className={classes.dialog__closeButton}>
          <X size={19} />
        </button>
      </div>
      <DialogContent className={classes.dialog__contentContainer} ref={dialogContentRef}>
        <div className={classes.dialog__content}>
          {generalError && (
            <div className={classes.errorContainer}>
              <p className={classes.errorMessage}>{generalError}</p>
            </div>
          )}
          {(crudType === crudTypes.UPDATE || crudType === crudTypes.READ) && (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: "8px", width: "100%", marginBottom: "16px" }}>
              {selectedCompany && (
                <Chip variant="light" startAdornment={<Factory />}>
                  {selectedCompany.name}
                </Chip>
              )}
              {selectedCostBundleType && (
                <Chip
                  variant="light"
                  startAdornment={
                    selectedCostBundleType.id === eventTypes.TIME_BASED ? (
                      <Clock />
                    ) : selectedCostBundleType.id === eventTypes.EVENT_BASED ? (
                      <Hash />
                    ) : selectedCostBundleType.id === eventTypes.RECURRING ? (
                      <Repeat />
                    ) : (
                      <CircleOff />
                    )
                  }
                >
                  {selectedCostBundleType.name}
                </Chip>
              )}
              {isRecurring && (
                <Chip variant="light" startAdornment={<Repeat />}>
                  {t('priceBundle.recurring')}
                </Chip>
              )}
              {crudType === crudTypes.READ && <Chip variant={priceBundle.account.isRevenue ? "success" : "danger"}>{priceBundle.account.isRevenue ? t('priceBundle.revenue') : t('priceBundle.cost')}</Chip>}
            </div>
          )}
          {/* --- Read --- */}
          {crudType === crudTypes.READ && priceBundle && (
            <>
              {priceBundle.description && <p className={classes.priceBundleDescription}>{priceBundle.description}</p>}
              {/* SelectedPriceDefinitions */}
              <PriceDefinitionsDisplay definitions={selectedPriceDefinitions} />
              {/* Fixed costs (TIME bundles only) */}
              {selectedFixedPriceDefinitions.length > 0 && (
                <>
                  <Divider style={{ marginBottom: "16px" }}>{t('priceBundle.fixedCosts')}</Divider>
                  <PriceDefinitionsDisplay definitions={selectedFixedPriceDefinitions} />
                </>
              )}
              {/* Total */}
              {selectedCostBundleType !== null && (selectedPriceDefinitions.length > 0 || selectedFixedPriceDefinitions.length > 0) && (
                <>
                  <Divider style={{ marginBottom: "16px" }}>{t('common.total')}</Divider>
                  <TotalCard />
                </>
              )}
            </>
          )}
          {/* --- Create/Update --- */}
          {(crudType === crudTypes.CREATE || crudType === crudTypes.UPDATE) && (
            <div className={classes.form}>
              {crudType === crudTypes.CREATE && (
                <>
                  <div className={classes.inputContainer}>
                    <div className={classes.labelContainer}>
                      <label className={classes.labelText}>{t('priceBundle.bundleType')}</label>
                      <p className={classes.inputError}>{selectedCostBundleTypeError}</p>
                    </div>
                    <Select placeholder={t('priceBundle.selectBundleType')} value={selectedCostBundleType?.id} onChange={setSelectedCostBundleType} error={selectedCostBundleTypeError !== null}>
                      <SelectTrigger>
                        {selectedCostBundleType && (
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                            <span style={{ fontSize: "16px", fontWeight: "500" }}>{selectedCostBundleType.name}</span>
                            <span style={{ fontSize: "12px", opacity: "0.8", color: "rgba(12,14,41,0.6)" }}>{selectedCostBundleType.description}</span>
                          </div>
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        {allCostBundleTypes.map((type) => (
                          <SelectItem key={type.id} value={type}>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                              <span style={{ fontSize: "16px", fontWeight: "500" }}>{type.name}</span>
                              <span style={{ fontSize: "12px", opacity: "0.8", color: "rgba(12,14,41,0.6)" }}>{type.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div style={{ display: "flex", flexDirection: "row", gap: "8px", width: "100%" }}>
                    <div className={classes.inputContainer}>
                      <div className={classes.labelContainer}>
                        <label className={classes.labelText}>{t('user.company')}*</label>
                        <p className={classes.inputError}>{selectedCompanyError}</p>
                      </div>
                      <Select searchable={true} placeholder={t('priceBundle.selectCompany')} value={selectedCompany?.id} onChange={changeSelectedCompany} error={selectedCompanyError !== null}>
                        <SelectTrigger>{selectedCompany?.name}</SelectTrigger>
                        <SelectContent>
                          {allCompanies.map((company) => (
                            <SelectItem key={company.id} value={company}>
                              {company.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className={classes.inputContainer}>
                      <div className={classes.labelContainer}>
                        <label className={classes.labelText}>{t('priceBundle.account')}</label>
                        <p className={classes.inputError}>{selectedAccountError}</p>
                      </div>
                      <Select searchable={true} placeholder={t('priceBundle.selectAccount')} value={selectedAccount?.id} onChange={setSelectedAccount} error={selectedAccountError !== null}>
                        <SelectTrigger>{selectedAccount?.name}</SelectTrigger>
                        <SelectContent>
                          {allAccounts
                            .find((company) => company.id === selectedCompany?.id)
                            ?.accountCategories.map((category) => (
                              <SelectGroup key={category.id}>
                                <SelectLabel>
                                  <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                    {category.name}
                                    <div style={{ height: "6px", width: "6px", borderRadius: "3px", backgroundColor: category.isRevenue ? "#5CC958" : "#D84949" }} />
                                  </div>
                                </SelectLabel>
                                {category.accounts.map((account) => (
                                  <SelectItem key={account.id} value={account}>
                                    {account.name}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </>
              )}
              {crudType === crudTypes.UPDATE && (
                <div className={classes.inputContainer}>
                  <div className={classes.labelContainer}>
                    <label className={classes.labelText}>{t('priceBundle.account')}</label>
                    <p className={classes.inputError}>{selectedAccountError}</p>
                  </div>
                  {/* <SearchableSelectExample /> */}
                  <Select searchable={true} placeholder={t('priceBundle.selectAccount')} value={selectedAccount?.id} onChange={setSelectedAccount} error={selectedAccountError !== null}>
                    <SelectTrigger>{selectedAccount?.name}</SelectTrigger>
                    <SelectContent>
                      {allAccounts
                        .find((company) => company.id === selectedCompany?.id)
                        ?.accountCategories.map((category) => (
                          <SelectGroup key={category.id}>
                            <SelectLabel>
                              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                {category.name}
                                <div style={{ height: "6px", width: "6px", borderRadius: "3px", backgroundColor: category.isRevenue ? "#5CC958" : "#D84949" }} />
                              </div>
                            </SelectLabel>
                            {category.accounts.map((account) => (
                              <SelectItem key={account.id} value={account}>
                                {account.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "row", gap: "8px", width: "100%" }}>
                <div className={classes.inputContainer}>
                  <div className={classes.labelContainer}>
                    <label className={classes.labelText}>{t('priceBundle.reference')}</label>
                    <p className={classes.inputError}>{referenceError}</p>
                  </div>
                  <Input type="text" value={reference} onChange={(event) => changeReference(event)} error={referenceError !== null} placeholder="PB0001" />
                </div>
                <div className={classes.inputContainer}>
                  <div className={classes.labelContainer}>
                    <label className={classes.labelText}>{t('common.name')}*</label>
                    <p className={classes.inputError}>{nameError}</p>
                  </div>
                  <Input type="text" value={name} onChange={(event) => changeName(event)} error={nameError !== null} placeholder={t('priceBundle.priceBundleName')} maxLength="50" />
                </div>
              </div>
              <div className={classes.inputContainer}>
                <div className={classes.labelContainer}>
                  <label className={classes.labelText}>{t('common.description')}</label>
                  <p className={classes.inputError}>{descriptionError}</p>
                </div>
                <Textarea style={{ height: "4.125rem" }} value={description} onChange={(event) => changeDescription(event)} />
              </div>
              <div className={classes.inputContainer}>
                <div className={classes.labelContainer}>
                  <label className={classes.labelText}>{t('priceBundle.selectPriceDefinitions')}</label>
                  <p className={classes.inputError}>{selectedPriceDefinitionsError}</p>
                </div>
                <div className={classes.addPriceDefinitionContainer}>
                  <div
                    ref={priceDefOpenDropdownButtonRef}
                    className={`${classes.selectTriggerInput} ${selectedPriceDefinitionsError !== null ? classes.selectTriggerInputError : ""}`}
                    onClick={() => setPriceDefinitionDropdownOpen(true)}
                  >
                    <input
                      type="text"
                      value={priceDefinitionSearchValue}
                      onChange={(e) => setPriceDefinitionSearchValue(e.target.value)}
                      onFocus={() => setPriceDefinitionDropdownOpen(true)}
                      className={classes.selectTriggerInputField}
                      placeholder={t('priceBundle.priceDefinitionName')}
                    />
                    <button
                      type="button"
                      className={classes.selectTriggerChevronButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPriceDefinitionDropdownOpen(!priceDefinitionDropdownOpen);
                      }}
                    >
                      <ChevronDown className={`${classes.selectTriggerIcon} ${priceDefinitionDropdownOpen ? classes.selectTriggerIconOpen : ""}`} />
                    </button>
                  </div>
                  {priceDefinitionDropdownOpen &&
                    ReactDOM.createPortal(
                      <div
                        ref={priceDefDropdownRef}
                        className={classes.dropdownContainer}
                        style={{ top: priceDefDropdownPosition.top, left: priceDefDropdownPosition.left, width: priceDefDropdownWidth }}
                      >
                        {!selectedCompany ? (
                          <div className={classes.dropdown__emptyState}>
                            <CircleOff className={classes.emptyState__icon} />
                            <p className={classes.emptyState__text}>{t('priceBundle.selectCompanyForPriceDefs')}</p>
                          </div>
                        ) : allPriceDefinitions
                            .filter((priceDefinition) => priceDefinition.companyId === selectedCompany?.id)
                            ?.filter((priceDefinition) => priceDefinition.name.toLowerCase().includes(priceDefinitionSearchValue.toLowerCase())).length > 0 ? (
                          allPriceDefinitions
                            .filter((priceDefinition) => priceDefinition.companyId === selectedCompany?.id)
                            .filter((priceDefinition) => priceDefinition.name.toLowerCase().includes(priceDefinitionSearchValue.toLowerCase()))
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((priceDefinition, index) => (
                              <button className={classes.dropdown__menuItem} onClick={() => handlePriceDefinitionSelection(priceDefinition)} key={"PriceDef_" + index}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {priceDefinition.name}
                                  {priceDefinition.companyName && <span style={{ fontSize: "14px", color: "#666" }}>- {priceDefinition.companyName}</span>}
                                </div>
                                <SelectedIndicator isSelected={selectedPriceDefinitions.some((pd) => pd.id === priceDefinition.id)} />
                              </button>
                            ))
                        ) : (
                          <div className={classes.dropdown__emptyState}>
                            <CircleOff className={classes.emptyState__icon} />
                            <p className={classes.emptyState__text}>{t('priceBundle.noPriceDefinitions')}</p>
                          </div>
                        )}
                      </div>,
                      document.body
                    )}
                </div>

                {/* Show selected price definitions */}
                {selectedPriceDefinitions.length > 0 && (
                  <div className={classes.selectedPriceDefinitionsContainer}>
                    <div className={classes.selectedPriceDefinition__accounts}>
                      {selectedPriceDefinitions
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((priceDefinition) => {
                          return (
                            <div key={priceDefinition.id} className={classes.accounts__accountContainer}>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
                                <p className={classes.accounts__accountName}>{priceDefinition.name}</p>
                                <Chip size="small">
                                  {/* find priceDefinition.entries[] where isCurrentPrice = true */}
                                  {priceDefinition.currentPrice?.currencySymbol || ""}
                                  {formatPrice(priceDefinition.currentPrice?.price) || ""}/{priceDefinition.currentPrice?.unitOfMeasurement || ""}
                                </Chip>
                              </div>

                              <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                                <Input
                                  type="text"
                                  value={multiplierInputValues[priceDefinition.id]}
                                  style={{ width: "100px", textAlign: "right" }}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(",", ".");
                                    if (value === "" || /^\d*\.?\d*$/.test(value)) {
                                      setPriceDefinitionMultiplier(priceDefinition.id, value);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    if (e.target.value === "" || isNaN(parseFloat(e.target.value))) {
                                      setPriceDefinitionMultiplier(priceDefinition.id, "1");
                                    }
                                  }}
                                  placeholder={t('priceBundle.multiplier')}
                                />
                                <Button iconButton variant="secondary" startIcon={<Trash2 />} size="small" onClick={() => handlePriceDefinitionSelection(priceDefinition)} />
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
              {/* Fixed costs (TIME bundles only): applied once per measurement, not scaled by duration */}
              {selectedCostBundleType?.id === eventTypes.TIME_BASED && (
                <div className={classes.inputContainer}>
                  <div className={classes.labelContainer}>
                    <label className={classes.labelText}>{t('priceBundle.fixedCosts')}</label>
                  </div>
                  <div className={classes.addPriceDefinitionContainer}>
                    <div ref={fixedPriceDefOpenDropdownButtonRef} className={classes.selectTriggerInput} onClick={() => setFixedPriceDefinitionDropdownOpen(true)}>
                      <input
                        type="text"
                        value={fixedPriceDefinitionSearchValue}
                        onChange={(e) => setFixedPriceDefinitionSearchValue(e.target.value)}
                        onFocus={() => setFixedPriceDefinitionDropdownOpen(true)}
                        className={classes.selectTriggerInputField}
                        placeholder={t('priceBundle.priceDefinitionName')}
                      />
                      <button
                        type="button"
                        className={classes.selectTriggerChevronButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFixedPriceDefinitionDropdownOpen(!fixedPriceDefinitionDropdownOpen);
                        }}
                      >
                        <ChevronDown className={`${classes.selectTriggerIcon} ${fixedPriceDefinitionDropdownOpen ? classes.selectTriggerIconOpen : ""}`} />
                      </button>
                    </div>
                    {fixedPriceDefinitionDropdownOpen &&
                      ReactDOM.createPortal(
                        <div
                          ref={fixedPriceDefDropdownRef}
                          className={classes.dropdownContainer}
                          style={{ top: fixedPriceDefDropdownPosition.top, left: fixedPriceDefDropdownPosition.left, width: fixedPriceDefDropdownWidth }}
                        >
                          {!selectedCompany ? (
                            <div className={classes.dropdown__emptyState}>
                              <CircleOff className={classes.emptyState__icon} />
                              <p className={classes.emptyState__text}>{t('priceBundle.selectCompanyForPriceDefs')}</p>
                            </div>
                          ) : allPriceDefinitions
                              .filter((priceDefinition) => priceDefinition.companyId === selectedCompany?.id)
                              ?.filter((priceDefinition) => priceDefinition.name.toLowerCase().includes(fixedPriceDefinitionSearchValue.toLowerCase())).length > 0 ? (
                            allPriceDefinitions
                              .filter((priceDefinition) => priceDefinition.companyId === selectedCompany?.id)
                              .filter((priceDefinition) => priceDefinition.name.toLowerCase().includes(fixedPriceDefinitionSearchValue.toLowerCase()))
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((priceDefinition, index) => (
                                <button className={classes.dropdown__menuItem} onClick={() => handleFixedPriceDefinitionSelection(priceDefinition)} key={"FixedPriceDef_" + index}>
                                  <div style={{ display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {priceDefinition.name}
                                    {priceDefinition.companyName && <span style={{ fontSize: "14px", color: "#666" }}>- {priceDefinition.companyName}</span>}
                                  </div>
                                  <SelectedIndicator isSelected={selectedFixedPriceDefinitions.some((pd) => pd.id === priceDefinition.id)} />
                                </button>
                              ))
                          ) : (
                            <div className={classes.dropdown__emptyState}>
                              <CircleOff className={classes.emptyState__icon} />
                              <p className={classes.emptyState__text}>{t('priceBundle.noPriceDefinitions')}</p>
                            </div>
                          )}
                        </div>,
                        document.body
                      )}
                  </div>

                  {/* Show selected fixed cost price definitions */}
                  {selectedFixedPriceDefinitions.length > 0 && (
                    <div className={classes.selectedPriceDefinitionsContainer}>
                      <div className={classes.selectedPriceDefinition__accounts}>
                        {selectedFixedPriceDefinitions
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((priceDefinition) => {
                            return (
                              <div key={priceDefinition.id} className={classes.accounts__accountContainer}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
                                  <p className={classes.accounts__accountName}>{priceDefinition.name}</p>
                                  <Chip size="small">
                                    {priceDefinition.currentPrice?.currencySymbol || ""}
                                    {formatPrice(priceDefinition.currentPrice?.price) || ""}/{priceDefinition.currentPrice?.unitOfMeasurement || ""}
                                  </Chip>
                                </div>

                                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                                  <Input
                                    type="text"
                                    value={fixedMultiplierInputValues[priceDefinition.id]}
                                    style={{ width: "100px", textAlign: "right" }}
                                    onChange={(e) => {
                                      const value = e.target.value.replace(",", ".");
                                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                                        setFixedPriceDefinitionMultiplier(priceDefinition.id, value);
                                      }
                                    }}
                                    onBlur={(e) => {
                                      if (e.target.value === "" || isNaN(parseFloat(e.target.value))) {
                                        setFixedPriceDefinitionMultiplier(priceDefinition.id, "1");
                                      }
                                    }}
                                    placeholder={t('priceBundle.multiplier')}
                                  />
                                  <Button iconButton variant="secondary" startIcon={<Trash2 />} size="small" onClick={() => handleFixedPriceDefinitionSelection(priceDefinition)} />
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Total */}
              {selectedCostBundleType !== null && (selectedPriceDefinitions.length > 0 || selectedFixedPriceDefinitions.length > 0) && (
                <>
                  <Divider>{t('common.total')}</Divider>
                  <TotalCard />
                </>
              )}
            </div>
          )}
          {disclaimer && (
            <div className={classes.disclaimerContainer}>
              <p className={classes.disclaimerMessage}>{disclaimer}</p>
            </div>
          )}
        </div>
      </DialogContent>
      {(crudType === crudTypes.CREATE || crudType === crudTypes.UPDATE) && (
        <DialogActions sx={{ display: "flex", justifyContent: "space-between", paddingLeft: "16px", paddingRight: "16px", paddingBottom: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {crudType === crudTypes.UPDATE && (
              <Button iconButton variant="secondary" size="small" onClick={() => setConfirmDeleteBundle(true)} startIcon={<Trash2 />} />
            )}
            {crudType === crudTypes.CREATE && (
              <div className={classes.inputContainer} style={{ width: "fit-content" }}>
                <CustomCheckbox checked={createAnotherBundle} onChange={setCreateAnotherBundle} label={t('priceBundle.createAnother')} />
              </div>
            )}
          </div>
          <Button onClick={() => handleSubmit()}>{getSubmitLabel()}</Button>
        </DialogActions>
      )}
    </Dialog>

    {/* Confirm Delete Dialog */}
    <Dialog open={confirmDeleteBundle} onClose={() => setConfirmDeleteBundle(false)} classes={{ paper: classes.confirmDialogPaper }}>
      <div className={classes.dialog__head}>
        <DialogTitle className={classes.dialog__title}>{t('priceBundle.deletePriceBundle')}</DialogTitle>
        <button onClick={() => setConfirmDeleteBundle(false)} className={classes.dialog__closeButton}>
          <X size={19} />
        </button>
      </div>
      <DialogContent className={classes.dialog__contentContainer}>
        <p style={{ margin: 0, fontSize: "16px", color: "#0C0E29" }}>
          {t('priceBundle.confirmDelete', { name: priceBundle?.name })}
        </p>
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "flex-end", gap: "8px", paddingLeft: "16px", paddingRight: "16px", paddingBottom: "16px" }}>
        <Button variant="secondary" size="small" onClick={() => setConfirmDeleteBundle(false)}>{t('common.cancel')}</Button>
        <Button variant="destructive" size="small" onClick={handleDeletePriceBundle}>{t('common.delete')}</Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

const useStyles = makeStyles(() => ({
  "@keyframes slideInFromRight": {
    "0%": {
      opacity: 0,
      transform: "translateX(20px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateX(0)",
    },
  },
  "@keyframes slideOutToRight": {
    "0%": {
      opacity: 1,
      transform: "translateX(0)",
    },
    "100%": {
      opacity: 0,
      transform: "translateX(20px)",
    },
  },
  "@keyframes fadeIn": {
    "0%": {
      opacity: 0,
    },
    "100%": {
      opacity: 1,
    },
  },
  slideInFromRight: {
    animation: "$slideInFromRight 0.3s ease-out",
  },
  slideOutToRight: {
    animation: "$slideOutToRight 0.2s ease-in",
  },
  fadeIn: {
    animation: "$fadeIn 0.3s ease-out",
  },
  dialog: {
    margin: "auto",
  },

  dialogPaper: {
    width: "520px !important",
    height: "auto",
    borderRadius: "6px !important",
  },

  confirmDialogPaper: {
    width: "360px !important",
    height: "auto",
    borderRadius: "6px !important",
  },

  dialog__head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",

    padding: "16px 16px 0px 16px !important",
    marginBottom: "8px !important",
  },

  dialog__title: {
    fontSize: "24px !important",
    fontFamily: "Lato, sans-serif !important",
    fontWeight: "normal",

    padding: "0px !important",

    maxWidth: "calc(100% - 40px)",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },

  dialog__closeButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",

    padding: "3px !important",
    height: "25px !important",

    borderRadius: "3px",

    transition: "all 0.2s",

    "&:hover": {
      backgroundColor: "#D84949",
      color: "#F5F5F5",
    },

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  dialog__subtitle: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    color: "#0C0E29",

    margin: "0px 0px 16px 0px",
  },

  dialog__contentContainer: {
    padding: "0px 16px !important",
    marginBottom: "16px",

    // clean thin scrollbar
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#D6D6D6",
      borderRadius: "9999px",
      border: "2px solid transparent",
      backgroundClip: "content-box",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#BDBDBD",
    },
    "-ms-overflow-style": "auto",
    scrollbarWidth: "thin",
    scrollbarColor: "#D6D6D6 transparent",
  },

  dialog__content: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",

    margin: "0px !important",
  },

  errorContainer: {
    width: "100%",
    backgroundColor: "#FDF3F3",

    border: "1px solid #FBE5E5",
    borderRadius: "6px",

    marginBottom: "16px",
    padding: "12px",
  },

  errorMessage: {
    fontSize: "16px",
    color: "#D84949",
  },

  disclaimerContainer: {
    width: "100%",
    backgroundColor: "#F8F8F8",

    border: "1px solid #EDEDED",
    borderRadius: "6px",

    marginTop: "16px",
    padding: "12px",
  },

  disclaimerMessage: {
    fontSize: "16px",
    color: "#0C0E29",
  },

  priceBundleDescription: {
    fontSize: "16px",
    color: "#0C0E29",
    opacity: "0.8",

    margin: "0 0 1rem 0",
  },

  priceEntries: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  validFromDate: {
    margin: "0px 4px 4px 0",
    fontSize: "14px",
    color: "#666",
    textAlign: "right",
    fontWeight: "400",
  },

  priceEntry: {
    width: "100%",
    minHeight: "48px",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",

    padding: "8px",

    borderRadius: "8px",
    backgroundColor: "#F8F8F8",
    border: "1px solid #EDEDED",

    boxSizing: "border-box",

    transition: "all ease-in-out 0.2s",
  },

  priceInfo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "2px",
  },

  price: {
    margin: "0",
    fontSize: "16px",
    fontWeight: "500",
    color: "#0C0E29",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  },

  form: {
    width: "100%",

    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },

  subscriptionLimitContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  inputContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  labelContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  inputError: {
    fontSize: "14px",
    color: "#D84949",

    margin: "0",
  },

  textField: {
    height: "40px",
  },

  radioButtonContainer: {
    width: "100%",
    display: "flex",
    gap: "8px",
  },

  typeContainer: {
    width: "100%",
    display: "flex",
    gap: "8px",
  },

  radioButton: {
    width: "100%",
    minHeight: "60px",

    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: "12px",

    padding: "12px 16px",
    border: "1px solid #EDEDED",
    borderRadius: "8px",

    backgroundColor: "transparent",

    transition: "all ease-in-out 0.2s",

    "&:hover": {
      backgroundColor: "#F8F8F8",
      cursor: "pointer",
    },
  },

  radioButtonSelected: {
    backgroundColor: "var(--color-primary)",
    color: "white",

    "&:hover": {
      backgroundColor: "#a77d4f",
    },
  },

  radio: {
    width: "20px",
    height: "20px",

    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    boxSizing: "border-box",

    borderRadius: "50%",
    border: "1px solid #0C0E29",
    backgroundColor: "transparent",
  },

  radioButton__label: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",

    margin: "0",
  },

  saveButton: {
    height: "40px",
    padding: "0 16px",

    alignSelf: "end",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "var(--color-primary)",

    transition: "all 0.2s ease-in-out",

    userSelect: "none",

    "&:hover": {
      backgroundColor: "#a77d4f",
      cursor: "pointer",
    },
  },

  deleteButton: {
    height: "40px",
    padding: "0 16px",
    verticalAlign: "center",
    border: "1px solid #D84949",
    borderRadius: "4px",

    fontSize: "16px",
    color: "#D84949",
    backgroundColor: "transparent",

    transition: "all 0.2s ease-in-out",

    userSelect: "none",

    "&:hover": {
      backgroundColor: "#C73B3B",
      color: "white",
      border: "1px solid #C73B3B",
      cursor: "pointer",
    },
  },

  // Price Definition styles (adapted from ProjectionsDialog)
  addPriceDefinitionContainer: {
    width: "100%",
    position: "relative",
  },

  // SelectTrigger-like input styles
  selectTriggerInput: {
    padding: "8px 12px",
    border: "1px solid #DCDCDC",
    borderRadius: "4px",
    backgroundColor: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    transition: "border-color 0.2s",
    "&:hover": {
      borderColor: "#888",
    },
  },

  selectTriggerInputError: {
    borderColor: "#D84949 !important",
    "&:hover": {
      borderColor: "#D84949 !important",
    },
  },

  selectTriggerInputField: {
    border: "none",
    outline: "none",
    backgroundColor: "transparent",
    flex: 1,
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    cursor: "text",
    color: "#0C0E29",
    "&::placeholder": {
      color: "rgba(12, 14, 41, 0.6)",
    },
  },

  selectTriggerChevronButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "2px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transform: "translateX(2px)",
    "&:hover": {
      backgroundColor: "#F0F0F0",
    },
  },

  selectTriggerIcon: {
    width: "16px",
    height: "16px",
    transition: "transform 0.2s",
    cursor: "pointer",
    color: "#666",
  },

  selectTriggerIconOpen: {
    transform: "rotate(180deg)",
  },

  dropdownContainer: {
    width: "100%",
    minWidth: "160px",
    maxHeight: "200px",
    overflowY: "auto",
    position: "absolute",
    zIndex: 9998,
    backgroundColor: "#FFFFFF",
    borderRadius: "4px",
    border: "1px solid #DCDCDC",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
    display: "flex",
    flexDirection: "column",
    padding: "4px",

    "&::-webkit-scrollbar": {
      width: "4px",
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#989898",
      borderRadius: "2px",
    },

    "&::-webkit-scrollbar-track": {
      backgroundColor: "#F4F4F4",
      borderRadius: "2px",
    },

    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#7C7C7C",
    },
  },

  dropdown__menuItem: {
    width: "100%",
    minHeight: "32px",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0px 8px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",
    backgroundColor: "#FFFFFF",

    "&:hover": {
      backgroundColor: "#F4F4F4",
    },

    "&:active": {
      backgroundColor: "#EDEDED",
    },

    "&:focus": {
      outline: "none",
    },
  },

  dropdown__emptyState: {
    height: "fit-content",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    padding: "24px 0 16px 0",
  },

  emptyStateContainer: {
    height: "fit-content",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    padding: "24px 0 16px 0",
    marginTop: "16px",
  },

  emptyState__icon: {
    width: "24px",
    height: "24px",
    color: "#0C0E29",
    strokeWidth: "1.4px",
  },

  emptyState__text: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
    margin: "0",
  },

  selectedPriceDefinitionsContainer: {
    width: "100%",
    marginTop: "16px",
    padding: "16px",
    border: "1px solid #DCDCDC",
    borderRadius: "8px",
  },

  selectedPriceDefinition__accounts: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: ".5rem",
    maxHeight: "12.5rem",
    overflowY: "auto",
    overflowX: "hidden",

    "&::-webkit-scrollbar": {
      width: "4px",
      paddingLeft: "2px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#989898",
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#F4F4F4",
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#7C7C7C",
    },
  },

  accounts__accountContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
  },

  accounts__accountName: {
    width: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
  },

  totalContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "16px",
    padding: "16px",
    borderRadius: "8px",
    backgroundColor: "#F8F8F8",
    border: "1px solid #EDEDED",
    color: "#0C0E29",
  },

  totalText: {
    fontSize: "16px",
    color: "inherit",
    margin: "0",
  },

  // PriceDefinitionsDisplay styles
  priceDefinitionsContainer: {
    width: "100%",
    border: "1px solid #EDEDED",
    borderRadius: "8px",
    padding: "4px",
    marginBottom: "16px",
    display: "flex",
    flexDirection: "column",
  },

  priceDefinitionItem: {
    height: "40px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "4px 8px",
  },

  priceDefinitionName: {
    fontSize: "16px",
    color: "rgba(12,14,41)",
    margin: "0",
  },

  priceDefinitionDetails: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  multiplierContainer: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    color: "rgba(12,14,41,0.8)",
  },

  multiplierText: {
    margin: "0",
    fontSize: "16px",
    color: "inherit",
  },

  multiplierIcon: {
    width: "12px",
    height: "12px",
    strokeWidth: "1.2px",
    color: "inherit",
  },
}));
